/* Tu perfil — página propia (pantalla 4a del rediseño).
 *
 * Antes era una caja apretada sobre el listado y vivía en el `localStorage` del navegador. Aquí es
 * una página con una promesa concreta: DE ESTO SALE EL ENCAJE. De ahí las dos decisiones que la
 * gobiernan y que conviene no deshacer sin querer:
 *
 * 1. CADA CAMPO DICE QUÉ CAMBIA SI LO LLENAS. No hay un solo rótulo suelto: debajo de cada sección
 *    va la consecuencia («lo que exceda tu máximo baja mucho en el orden»). Un formulario de
 *    preferencias sin consecuencias declaradas se llena a ojo, y entonces la afinidad que sale de él
 *    tampoco significa nada.
 *
 * 2. LA COLUMNA DERECHA ES EL EFECTO, EN VIVO. Cuántos procesos te sirven ahora mismo y cuáles son
 *    los tres mejores, recalculado a cada edición con `perfil_simulado`. Es lo que convierte el
 *    formulario en algo que se puede afinar en vez de rellenar.
 *
 * Lo que NO hace, y es deliberado: no calcula nada. El conteo, el orden y los tres mejores salen de
 * `POST /api/oportunidades/buscar`, la misma llamada que usa la bandeja. Si esta página tuviera su
 * propia idea de qué encaja, volveríamos al gemelo que la fase 3 vino a matar.
 *
 * El guardado es automático (rebote de 800 ms en `estado/perfil.tsx`) y por eso aquí NO está el
 * «Descartar cambios» del diseño: no hay nada que descartar cuando ya se guardó solo, y un botón
 * que promete deshacer sin poder hacerlo es peor que no tenerlo. Lo que sí queda es «Guardar y ver
 * mis oportunidades», que fuerza el guardado pendiente y lleva a la bandeja.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { enviar, ErrorApi, obtener, subir } from '../api/cliente'
import { entero, normalizarObjeto, pesos, porcentaje } from '../api/formato'
import { REGLAS } from '../api/tipos'
import type { ClaveRegla, Meta, Perfil, RespuestaBusqueda, RupDestilado } from '../api/tipos'
import { usePerfil } from '../estado/perfil'
import { IconoMas, IconoQuitar, IconoSi } from '../ui/Iconos'
import './Perfil.css'

export function PerfilPagina() {
  const { perfil, rup, estado, error, editar, guardarYa, ponerRup, guardadoEn } = usePerfil()
  const navegar = useNavigate()

  const [meta, setMeta] = useState<Meta | null>(null)
  useEffect(() => {
    obtener<Meta>('/api/oportunidades/meta').then(setMeta).catch(() => setMeta(null))
  }, [])

  // Anclas para «Completar ahora»: lleva al campo que falta, no a la sección genérica.
  const anclas = {
    actividad: useRef<HTMLDivElement>(null),
    territorio: useRef<HTMLDivElement>(null),
    capacidad: useRef<HTMLDivElement>(null),
  }

  const faltante = queFalta(perfil, rup)
  const faltantes = contarFaltantes(perfil, rup)
  const completitud = Math.round(((DIMENSIONES.length - faltantes) / DIMENSIONES.length) * 100)

  return (
    <div className="perfil">
      <div className="perfil-cuerpo">
        {/* ── columna de edición ─────────────────────────────────────────── */}
        <div className="perfil-edicion">
          <header>
            <h1>Tu perfil</h1>
            <p className="perfil-promesa">
              De esto sale la <b>afinidad</b>: mientras más preciso, mejor ordenadas te llegan las
              oportunidades. Nada de esto se comparte con las entidades.
            </p>
            {/* Consecuencia de que el perfil sea de la CUENTA y no del navegador. Quien comparte
                cuenta comparte perfil, y descubrirlo por sorpresa es descubrir que otra persona te
                cambió el orden de las oportunidades. */}
            <p className="perfil-nota">
              Se guarda en tu cuenta, así que lo verás desde cualquier navegador — y quien comparta
              esta cuenta comparte este perfil.
            </p>

            <Completitud pct={completitud} faltante={faltante} cuantas={faltantes}
                         onCompletar={() => {
                           const ancla = faltante ? anclas[faltante.donde].current : null
                           ancla?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                           ancla?.querySelector('input')?.focus()
                         }} />
          </header>

          <div ref={anclas.actividad}>
            <QueHace perfil={perfil} rup={rup} editar={editar} ponerRup={ponerRup} />
          </div>

          <div ref={anclas.territorio}>
            <DondeTrabajas perfil={perfil} meta={meta} editar={editar} />
          </div>

          <div ref={anclas.capacidad}>
            <CuantoPuedes perfil={perfil} rup={rup} editar={editar} />
          </div>

          <LoQueNoQuieresVer perfil={perfil} editar={editar} />
          <CuandoTeAvisamos perfil={perfil} editar={editar} />

          <footer className="perfil-pie">
            <button type="button" className="boton-primario"
                    onClick={() => { void guardarYa().then(() => navegar('/')) }}>
              Guardar y ver mis oportunidades
            </button>
            <span className="perfil-guardado">
              {estado === 'guardando' ? 'Guardando…'
                : estado === 'conflicto' ? 'Cambió en otra pestaña — recarga para no pisarlo'
                : estado === 'error' ? (error ?? 'No se pudo guardar')
                : hace(guardadoEn)}
            </span>
          </footer>
        </div>

        {/* ── columna del efecto ─────────────────────────────────────────── */}
        <ElEfecto perfil={perfil} rup={rup} />
      </div>
    </div>
  )
}

/* ── completitud ──────────────────────────────────────────────────────────────
   El porcentaje solo no sirve para nada: «80% listo» no dice qué hacer. Lo que hace trabajo aquí
   es el faltante CONCRETO y qué se pierde por no tenerlo. */

type Donde = 'actividad' | 'territorio' | 'capacidad'
type Faltante = { donde: Donde; que: string; porque: string }

const DIMENSIONES = ['actividad', 'territorio', 'capacidad_max', 'capacidad_min'] as const

function tieneActividad(perfil: Perfil, rup: RupDestilado | null): boolean {
  return Boolean(rup?.unspsc?.length || perfil.sectores.length || perfil.temas_manuales.length)
}

/** De dónde salió el perfil que hay cargado — el discriminador de la tarjeta de experiencia.
 *
 * ⚠ Bifurca por `fuente`, NO por cuánto trae el payload, y esa es toda la razón de que exista. El
 * perfil del registro mercantil (`'rues'`) llega con la experiencia y las clases UNSPSC vacías A
 * PROPÓSITO: esa empresa no ha contratado con el Estado. Mientras la tarjeta preguntaba
 * `!exp?.n_contratos`, un perfil RUES encontrado se leía igual que «no busqué nada» y la persona
 * veía otra vez el formulario, sin error y sin resultado.
 */
function estadoNit(rup: RupDestilado | null): 'vacio' | 'rues' | 'rup' | 'secop' {
  if (rup?.fuente === 'rues') return 'rues'
  if (!rup?.perfil?.experiencia?.n_contratos) return 'vacio'
  return rup.fuente === 'rup' ? 'rup' : 'secop'
}

/**
 * Cuántas de las cuatro cosas que pesan en la afinidad faltan en un perfil.
 *
 * EXPORTADA a propósito: la página de la cartera pinta una barra de completitud por empresa, y esta
 * es LA definición de «qué le falta a un perfil». Duplicarla allí —o calcularla en el servidor para
 * el endpoint de la cartera— crearía dos definiciones de lo mismo que se desincronizan sin que nada
 * avise, que es el gemelo que este proyecto ya retiró del puntaje. Un dueño, uno solo.
 */
export function contarFaltantes(perfil: Perfil, rup: RupDestilado | null): number {
  return [
    !tieneActividad(perfil, rup),
    perfil.deps.length === 0,
    perfil.max === null,
    perfil.min === null,
  ].filter(Boolean).length
}

/** El primer hueco por orden de PESO en la afinidad, no por orden de aparición en la página. */
function queFalta(perfil: Perfil, rup: RupDestilado | null): Faltante | null {
  if (!tieneActividad(perfil, rup)) {
    return {
      donde: 'actividad', que: 'decirnos qué hace tu empresa',
      porque: 'Es lo que más pesa en la afinidad: sin eso el orden es el general, no el tuyo.',
    }
  }
  if (perfil.max === null) {
    return {
      donde: 'capacidad', que: 'declarar tu capacidad máxima',
      porque: 'Sin ella no podemos descartar los procesos que no podrías ejecutar.',
    }
  }
  if (perfil.deps.length === 0) {
    return {
      donde: 'territorio', que: 'decir dónde trabajas',
      porque: 'No esconde nada, pero sube los procesos de tus departamentos.',
    }
  }
  if (perfil.min === null) {
    return {
      donde: 'capacidad', que: 'declarar tu contrato mínimo',
      porque: 'Baja los que no pagan las horas de preparar la propuesta.',
    }
  }
  return null
}

function Completitud({ pct, faltante, cuantas, onCompletar }: {
  pct: number
  faltante: Faltante | null
  /** Cuántas dimensiones faltan EN TOTAL. Se nombra una sola, pero el número tiene que cuadrar. */
  cuantas: number
  onCompletar: () => void
}) {
  return (
    <div className="completitud">
      <div className="completitud-texto">
        <p className="completitud-cabeza">
          <b className="cifra">{porcentaje(pct)}</b> <span>de tu perfil está listo</span>
        </p>
        <div className="completitud-barra" aria-hidden="true">
          <i style={{ width: `${pct}%` }} />
        </div>
        <p className="completitud-falta">
          {/* «Falta una sola cosa» solo si de verdad falta una sola. El diseño trae esa frase
              porque su ejemplo está al 80 %; escribirla siempre convertía «25 % listo» en «falta
              una sola cosa», que es falso y además desmiente a la barra que está justo encima. */}
          {faltante
            ? <>{cuantas === 1 ? 'Falta una sola cosa: ' : 'Empieza por aquí: '}
                <b>{faltante.que}</b>. {faltante.porque}
                {cuantas > 1 && <> Después quedan {cuantas - 1} {cuantas === 2 ? 'cosa' : 'cosas'}.</>}</>
            : 'Está completo. Las correcciones que hagas en una ficha se guardan aquí.'}
        </p>
      </div>
      {faltante && (
        <button type="button" className="boton-primario" onClick={onCompletar}>
          Completar ahora
        </button>
      )}
    </div>
  )
}

/* ── qué hace tu empresa ──────────────────────────────────────────────────── */

function QueHace({ perfil, rup, editar, ponerRup }: {
  perfil: Perfil
  rup: RupDestilado | null
  editar: (c: Partial<Perfil>) => void
  ponerRup: (r: RupDestilado, sugerido?: Partial<Perfil>) => void
}) {
  const [verClases, setVerClases] = useState(false)
  const [nuevoTema, setNuevoTema] = useState('')
  const [agregando, setAgregando] = useState(false)

  return (
    <Seccion titulo="Qué hace tu empresa"
             sub="Es lo que más pesa en la afinidad. Lo sacamos de tu RUP y de lo que ya ejecutaste en
                  SECOP; lo que hagas por fuera nos lo tienes que decir tú.">
      <div className="tarjetas">
        <FuenteActividad rup={rup} onRup={ponerRup} verClases={verClases} setVerClases={setVerClases} />
        <ExperienciaSecop rup={rup} perfil={perfil} onRup={ponerRup} />
      </div>

      {/* Borde punteado: es lo único de esta sección que escribe la persona, no una fuente. */}
      <div className="manuales">
        <div className="manuales-cabeza">
          <span className="rotulo rotulo-oro">Lo que haces y no aparece en SECOP</span>
          <span className="manuales-origen">se guarda desde las fichas, cuando corriges un veredicto</span>
        </div>
        <div className="chips">
          {perfil.temas_manuales.map((t) => (
            <button key={t} type="button" className="chip-quitable"
                    onClick={() => editar({
                      temas_manuales: perfil.temas_manuales.filter((x) => x !== t),
                      sectores: perfil.sectores.filter((x) => x !== t),
                    })}>
              {t} <IconoQuitar />
              <span className="oculto-visual">quitar</span>
            </button>
          ))}
          {agregando ? (
            <form className="chip-nuevo"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const t = nuevoTema.trim()
                    if (t && !perfil.temas_manuales.includes(t)) {
                      editar({
                        temas_manuales: [...perfil.temas_manuales, t],
                        sectores: perfil.sectores.includes(t) ? perfil.sectores
                          : [...perfil.sectores, t],
                      })
                    }
                    setNuevoTema('')
                    setAgregando(false)
                  }}>
              <input autoFocus value={nuevoTema} onChange={(e) => setNuevoTema(e.target.value)}
                     onBlur={() => { setNuevoTema(''); setAgregando(false) }}
                     placeholder="mantenimiento de sedes educativas" aria-label="Tema nuevo" />
            </form>
          ) : (
            <button type="button" className="chip-agregar" onClick={() => setAgregando(true)}>
              <IconoMas /> agregar un tema
            </button>
          )}
        </div>
      </div>
    </Seccion>
  )
}

function FuenteActividad({ rup, onRup, verClases, setVerClases }: {
  rup: RupDestilado | null
  onRup: (r: RupDestilado, sugerido?: Partial<Perfil>) => void
  verClases: boolean
  setVerClases: (v: boolean) => void
}) {
  const [subiendo, setSubiendo] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)
  const archivo = useRef<HTMLInputElement>(null)

  const cargar = useCallback(async (f: File) => {
    setSubiendo(true)
    setFallo(null)
    try {
      const datos = new FormData()
      datos.append('archivo', f)
      const r = await subir<RupDestilado & { ok?: boolean; sectores?: string[] }>('/api/rup', datos)
      onRup({ ...r, fuente: 'rup' }, {
        ...(r.nit ? { nit: r.nit } : {}),
        ...(r.empresa ? { razon_social: r.empresa } : {}),
        ...(r.sectores?.length ? { sectores: r.sectores } : {}),
        ...(r.capacidad_max_pesos ? { max: Math.round(r.capacidad_max_pesos / 1e6) } : {}),
      })
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message : String(e))
    } finally {
      setSubiendo(false)
    }
  }, [onRup])

  if (!rup || rup.fuente !== 'rup') {
    return (
      <div className="tarjeta tarjeta-vacia">
        <span className="rotulo">Tu RUP</span>
        <p>
          El certificado del RUP trae las clases UNSPSC que declaras. Es la fuente más precisa de
          la afinidad: con él dejamos de comparar por sectores generales.
        </p>
        <div className="tarjeta-acciones">
          <button type="button" className="accion" disabled={subiendo}
                  onClick={() => archivo.current?.click()}>
            {subiendo ? 'Leyendo el PDF…' : 'Cargar el PDF del RUP'}
          </button>
        </div>
        <input ref={archivo} type="file" accept="application/pdf" hidden
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void cargar(f) }} />
        {fallo && <p className="perfil-error">{fallo}</p>}
      </div>
    )
  }

  return (
    <div className="tarjeta">
      <div className="tarjeta-cabeza">
        <span className="pastilla-ok">RUP cargado</span>
      </div>
      <p>
        <b>{entero(rup.unspsc.length)} clases UNSPSC</b> leídas del PDF
        {rup.sectores?.length ? <> · {rup.sectores.slice(0, 4).join(', ').toLowerCase()}</> : null}
      </p>
      <div className="tarjeta-acciones">
        <button type="button" className="accion" onClick={() => setVerClases(!verClases)}
                aria-expanded={verClases}>
          {verClases ? 'Ocultar las clases' : `Ver las ${entero(rup.unspsc.length)} clases`}
        </button>
        <button type="button" className="accion" disabled={subiendo}
                onClick={() => archivo.current?.click()}>
          {subiendo ? 'Leyendo el PDF…' : 'Reemplazar PDF'}
        </button>
      </div>
      <input ref={archivo} type="file" accept="application/pdf" hidden
             onChange={(e) => { const f = e.target.files?.[0]; if (f) void cargar(f) }} />
      {verClases && (
        <ul className="clases">
          {rup.unspsc.map((c) => <li key={c} className="cifra">{c}</li>)}
        </ul>
      )}
      {fallo && <p className="perfil-error">{fallo}</p>}
    </div>
  )
}

function ExperienciaSecop({ rup, perfil, onRup }: {
  rup: RupDestilado | null
  perfil: Perfil
  onRup: (r: RupDestilado, sugerido?: Partial<Perfil>) => void
}) {
  const [nit, setNit] = useState(perfil.nit)
  const [buscando, setBuscando] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)
  const exp = rup?.perfil?.experiencia

  const estimar = useCallback(async () => {
    const limpio = nit.replace(/\D/g, '')
    if (!limpio) return
    setBuscando(true)
    setFallo(null)
    try {
      const r = await obtener<RupDestilado & { ok: boolean; mensaje?: string }>(
        `/api/perfil-nit?nit=${encodeURIComponent(limpio)}`)
      if (!r.ok) {
        setFallo(r.mensaje ?? 'No encontré esa empresa en SECOP.')
        return
      }
      // `r.fuente` y no el literal: el servidor responde 'rues' cuando la empresa no está en SECOP
      // y sí en el registro mercantil. Pisarlo con 'secop' borraba la única marca de procedencia y
      // hacía que la página dijera «estimado desde tu historial» a quien no tiene historial.
      onRup({ ...r, fuente: r.fuente ?? 'secop' }, {
        nit: limpio,
        ...(r.empresa ? { razon_social: r.empresa } : {}),
        ...(r.sectores?.length ? { sectores: r.sectores } : {}),
        ...(r.perfil?.departamento ? { deps: [r.perfil.departamento] } : {}),
      })
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message : String(e))
    } finally {
      setBuscando(false)
    }
  }, [nit, onRup])

  const estado = estadoNit(rup)

  if (estado === 'rues') {
    return (
      <div className="tarjeta">
        <span className="rotulo">Empresa encontrada en el registro mercantil</span>
        <p>
          <b>{rup?.empresa}</b> no tiene contratos ejecutados en SECOP, así que queda registrada con
          lo que declara en el RUES. Puedes seguir y navegar los procesos abiertos, pero{' '}
          <b>todavía no estamos calificando el encaje</b>: sin las clases UNSPSC no hay con qué
          comparar tu empresa contra cada proceso.
        </p>
        {rup?.sectores?.length ? (
          <div className="chips">
            {rup.sectores.slice(0, 6).map((s) => <span key={s} className="chip">{s}</span>)}
          </div>
        ) : null}
        <p className="perfil-nota">
          Cargar el RUP es lo que enciende la afinidad: son las clases que declaras las que ordenan
          los procesos por lo que tu empresa sabe hacer, en vez de por fecha de cierre.
        </p>
      </div>
    )
  }

  if (estado === 'vacio') {
    return (
      <div className="tarjeta tarjeta-vacia">
        <span className="rotulo">Experiencia en SECOP</span>
        <p>
          Escribe tu NIT y buscamos lo que tu empresa ya ejecutó: lo usamos para comparar cada
          proceso con lo que ya has hecho.
        </p>
        <form className="nit-fila" onSubmit={(e) => { e.preventDefault(); void estimar() }}>
          <input value={nit} onChange={(e) => setNit(e.target.value)} inputMode="numeric"
                 placeholder="900482117" aria-label="NIT de tu empresa" />
          {/* Deshabilitado sin dígitos: `estimar()` sale sin hacer nada con el campo vacío, y un
              botón que se pulsa y no pasa nada se lee como que la página está rota. */}
          <button type="submit" className="accion" disabled={buscando || !nit.replace(/\D/g, '')}>
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </form>
        {fallo && <p className="perfil-error">{fallo}</p>}
      </div>
    )
  }

  // `n_contratos` lo llenan las DOS fuentes y significan cosas distintas: en SECOP son contratos
  // ejecutados que encontramos nosotros; en el RUP son las experiencias que la empresa DECLARA en su
  // certificado. Rotularlas igual decía «encontrada en SECOP» sobre un número que sale del PDF —y que
  // ni siquiera coincide con el de SECOP: 18 declaradas contra 14 ejecutadas, medido en ago-2026.
  const deRup = estado === 'rup'
  return (
    <div className="tarjeta">
      <span className="rotulo">
        {deRup ? 'Experiencia declarada en tu RUP' : 'Experiencia encontrada en SECOP'}
      </span>
      <p>
        <b>{entero(exp?.n_contratos ?? 0)} contratos</b> {deRup ? 'declarados' : 'ejecutados'}
        {deRup
          ? (exp?.max_smmlv ? <> el mayor de <b>{entero(exp.max_smmlv)} SMMLV</b></> : null)
          : (exp?.total_pesos ? <> por <b>{pesos(exp.total_pesos)}</b></> : null)}. Los usamos para
        comparar cada proceso con lo que ya has hecho.
      </p>
      {rup?.sectores?.length ? (
        <div className="chips">
          {rup.sectores.slice(0, 6).map((s) => <span key={s} className="chip">{s}</span>)}
        </div>
      ) : null}
      {/* El aviso viene del servidor y dice lo que este atajo NO cubre. No se resume. */}
      {rup?.fuente === 'secop' && (
        <p className="perfil-nota">
          Estimado desde tu historial: es lo que la empresa YA hizo, no todo lo que puede hacer.
          Cargar el RUP añade las clases que declaras y no has ejecutado todavía.
        </p>
      )}
    </div>
  )
}

/* ── dónde trabajas ───────────────────────────────────────────────────────── */

function DondeTrabajas({ perfil, meta, editar }: {
  perfil: Perfil
  meta: Meta | null
  editar: (c: Partial<Perfil>) => void
}) {
  const [verTodos, setVerTodos] = useState(false)
  const disponibles = meta?.departamentos ?? []
  // Los propios primero y siempre visibles: uno que ya declaraste no puede quedar escondido detrás
  // de «+ otro departamento», o parecería que no está.
  const visibles = verTodos ? disponibles
    : [...perfil.deps, ...disponibles.filter((d) => !perfil.deps.includes(d))].slice(0, 8)

  const alternar = (d: string) => editar({
    deps: perfil.deps.includes(d) ? perfil.deps.filter((x) => x !== d) : [...perfil.deps, d],
  })

  return (
    <Seccion titulo="Dónde trabajas"
             sub="El territorio no esconde nada: solo desempata. Verás procesos de todo el país,
                  pero los de tus departamentos suben.">
      <div className="pildoras">
        {visibles.map((d) => {
          const puesto = perfil.deps.includes(d)
          return (
            <button key={d} type="button" className={`pildora${puesto ? ' puesta' : ''}`}
                    aria-pressed={puesto} onClick={() => alternar(d)}>
              {d} {puesto && <IconoSi />}
            </button>
          )
        })}
        {!verTodos && disponibles.length > visibles.length && (
          <button type="button" className="pildora-mas" onClick={() => setVerTodos(true)}>
            <IconoMas /> otro departamento
          </button>
        )}
      </div>
      <Palanca puesta={perfil.fuera_territorio}
               onCambiar={() => editar({ fuera_territorio: !perfil.fuera_territorio })}
               etiqueta="Muéstrame también fuera de mis territorios cuando el contrato lo justifique" />
    </Seccion>
  )
}

/* ── cuánto puedes ejecutar ───────────────────────────────────────────────── */

function CuantoPuedes({ perfil, rup, editar }: {
  perfil: Perfil
  rup: RupDestilado | null
  editar: (c: Partial<Perfil>) => void
}) {
  const mayor = rup?.perfil?.experiencia?.max_pesos ?? null
  const sugerido = rup?.capacidad_max_pesos ?? null

  return (
    <Seccion titulo="Cuánto puedes ejecutar"
             sub="Lo que exceda tu máximo baja mucho en el orden; lo que quede por debajo de tu
                  mínimo baja un poco, porque el margen no siempre paga las horas de propuesta.">
      <div className="campos">
        <CampoMillones rotulo="Contrato mínimo" valor={perfil.min}
                       onCambiar={(v) => editar({ min: v })} />
        <CampoMillones rotulo="Contrato máximo" valor={perfil.max} falta
                       onCambiar={(v) => editar({ max: v })} />
        <CampoNumero rotulo="Contratos a la vez" valor={perfil.contratos_a_la_vez}
                     onCambiar={(v) => editar({ contratos_a_la_vez: v })} />
        {mayor && sugerido ? (
          <p className="campos-nota">
            El mayor contrato que te encontramos es de <b>{pesos(mayor)}</b>. Como referencia de
            triaje sugerimos el doble, <b>{pesos(sugerido)}</b>, como máximo.
          </p>
        ) : null}
      </div>
      {/* Los dos primeros campos mueven el orden; el tercero no lo mueve todavía y hay que decirlo,
          o sería un campo que se llena creyendo que hace algo. */}
      <p className="perfil-nota">
        «Contratos a la vez» se guarda, pero todavía no cambia el orden: hoy no cruzamos tu carga de
        trabajo con lo que sigues.
      </p>
    </Seccion>
  )
}

function CampoMillones({ rotulo, valor, onCambiar, falta }: {
  rotulo: string
  valor: number | null
  onCambiar: (v: number | null) => void
  falta?: boolean
}) {
  const vacio = valor === null
  return (
    <label className={`campo-cifra${vacio && falta ? ' campo-falta' : ''}`}>
      <span className="rotulo">{rotulo}{vacio && falta ? ' · falta' : ''}</span>
      <span className="campo-caja">
        {/* El «$» y el «millones» solo tienen sentido alrededor de una cifra. Con el campo vacío
            estorban: dejan al marcador de posición en un hueco de 82 px, donde «sin declarar» se
            corta a «sin declara» — y un rótulo truncado se lee como un fallo, no como un aviso. */}
        {!vacio && <span aria-hidden="true">$</span>}
        <input type="number" min={0} inputMode="numeric" value={valor ?? ''}
               className={vacio ? 'ancho' : ''}
               placeholder="sin declarar"
               onChange={(e) => onCambiar(e.target.value === '' ? null : Number(e.target.value))} />
        {!vacio && <span className="campo-unidad">millones</span>}
      </span>
    </label>
  )
}

function CampoNumero({ rotulo, valor, onCambiar }: {
  rotulo: string
  valor: number | null
  onCambiar: (v: number | null) => void
}) {
  return (
    <label className="campo-cifra">
      <span className="rotulo">{rotulo}</span>
      <span className="campo-caja">
        <input type="number" min={0} inputMode="numeric" value={valor ?? ''} placeholder="—"
               onChange={(e) => onCambiar(e.target.value === '' ? null : Number(e.target.value))} />
      </span>
    </label>
  )
}

/* ── lo que no quieres ver ────────────────────────────────────────────────── */

function LoQueNoQuieresVer({ perfil, editar }: {
  perfil: Perfil
  editar: (c: Partial<Perfil>) => void
}) {
  const descartes = perfil.descartes_agg ?? {}
  const total = Object.values(descartes).reduce((a, b) => a + b, 0)
  const puestas = new Map(perfil.reglas_no_ver.map((r) => [r.clave, r]))

  // Se ofrecen las reglas cuyo motivo ya se repitió, y siempre las que ya están puestas: una regla
  // encendida no puede desaparecer de la pantalla porque hoy no haya descartes nuevos.
  const ofrecidas = REGLAS.filter((r) => (descartes[r.motivo] ?? 0) > 0 || puestas.has(r.clave))

  const cambiar = (clave: ClaveRegla, cambios: { activa?: boolean; valor?: number }) => {
    const def = REGLAS.find((r) => r.clave === clave)!
    const previa = puestas.get(clave) ?? { clave, activa: false, valor: def.porOmision }
    const siguiente = { ...previa, ...cambios }
    editar({
      reglas_no_ver: [...perfil.reglas_no_ver.filter((r) => r.clave !== clave), siguiente],
    })
  }

  if (total === 0 && ofrecidas.length === 0) {
    return (
      <Seccion titulo="Lo que no quieres ver"
               sub="Las reglas salen de tus descartes. Todavía no has descartado nada; cuando
                    descartes un proceso y digas por qué, aquí aparecerá la regla que te ahorra
                    volver a leer los parecidos." />
    )
  }

  const repetidos = Object.entries(descartes).filter(([, n]) => n > 1).length
  return (
    <Seccion titulo="Lo que no quieres ver"
             sub={`Salen de tus descartes: llevas ${entero(total)} y ${repetidos === 0
               ? 'ningún motivo se repite todavía'
               : repetidos === 1 ? 'un motivo se repite' : `${repetidos} motivos se repiten`}.
               Convertirlos en regla te ahorra volver a leerlos.`}>
      <div className="reglas">
        {ofrecidas.map((r) => {
          const puesta = puestas.get(r.clave)
          const valor = puesta?.valor ?? r.porOmision
          const activa = puesta?.activa ?? false
          return (
            <div key={r.clave} className="regla">
              <Palanca puesta={activa} onCambiar={() => cambiar(r.clave, { activa: !activa })}
                       etiqueta={r.texto(valor)} />
              <input className="regla-umbral" type="number" min={1} value={valor}
                     aria-label={`Umbral en ${r.unidad}`}
                     onChange={(e) => cambiar(r.clave, { valor: Number(e.target.value) || 1 })} />
              <span className="regla-veces">
                descartaste {entero(descartes[r.motivo] ?? 0, '0')} así
              </span>
            </div>
          )
        })}
      </div>
      {(descartes['No cumplo requisitos'] ?? 0) > 0 && (
        // El motivo más frecuente en las pruebas, y el único que NO se puede convertir en regla:
        // saber si cumples los habilitantes exige leer el pliego. Decirlo es mejor que ofrecer una
        // regla que no podría cumplir lo que promete.
        <p className="perfil-nota">
          Descartaste {entero(descartes['No cumplo requisitos'])} por no cumplir requisitos. Eso no
          se puede convertir en regla sin leer los pliegos: es lo que hará «Analizar el pliego».
        </p>
      )}
    </Seccion>
  )
}

/* ── cuándo te avisamos ───────────────────────────────────────────────────── */

const FRECUENCIAS = [
  { clave: 'diario', etiqueta: 'Resumen diario' },
  { clave: 'semanal', etiqueta: 'Semanal' },
  { clave: 'app', etiqueta: 'Solo en la app' },
] as const

function CuandoTeAvisamos({ perfil, editar }: {
  perfil: Perfil
  editar: (c: Partial<Perfil>) => void
}) {
  const avisos = perfil.avisos
  const porCorreo = avisos.frecuencia !== 'app'
  return (
    <Seccion titulo="Cuándo te avisamos"
             sub="Un correo al día como máximo, y solo de lo que pasa tu umbral de afinidad.">
      <div className="avisos">
        <div className="segmentado" role="group" aria-label="Frecuencia">
          {FRECUENCIAS.map((f) => (
            <button key={f.clave} type="button" aria-pressed={avisos.frecuencia === f.clave}
                    className={avisos.frecuencia === f.clave ? 'puesto' : ''}
                    onClick={() => editar({ avisos: { ...avisos, frecuencia: f.clave } })}>
              {f.etiqueta}
            </button>
          ))}
        </div>
        <label className="avisos-umbral">
          Avísame desde una afinidad de
          <input type="number" min={0} max={100} value={avisos.umbral_afinidad}
                 aria-label="Umbral de afinidad"
                 onChange={(e) => editar({
                   avisos: { ...avisos, umbral_afinidad: Math.max(0, Math.min(100, Number(e.target.value) || 0)) },
                 })} />
          hacia arriba
        </label>
        <Palanca puesta={avisos.cambios_en_seguidos}
                 onCambiar={() => editar({
                   avisos: { ...avisos, cambios_en_seguidos: !avisos.cambios_en_seguidos },
                 })}
                 etiqueta="…y cuando algo que sigo cambie de fecha o presupuesto" />
      </div>
      {/* NO hay infraestructura de correo en el repositorio (ver docs/frontend.md). La preferencia
          se guarda de verdad; el envío no existe todavía. Decirlo aquí, en la pantalla donde se
          elige, y no en una nota de versión que nadie lee: alguien que confíe en el correo diario
          para no perder un cierre se queda esperándolo. */}
      {porCorreo && (
        <p className="perfil-aviso-honesto">
          <b>El envío por correo todavía no está conectado.</b> Guardamos tu preferencia y la
          aplicaremos cuando lo esté; mientras tanto, revisa la bandeja. No cuentes con este correo
          para no perderte un cierre.
        </p>
      )}
    </Seccion>
  )
}

/* ── la columna del efecto ────────────────────────────────────────────────── */

function ElEfecto({ perfil, rup }: { perfil: Perfil; rup: RupDestilado | null }) {
  const [datos, setDatos] = useState<RespuestaBusqueda | null>(null)
  const [universo, setUniverso] = useState<number | null>(null)
  const [conMaximo, setConMaximo] = useState<number | null>(null)

  // El perfil ENTERO como `perfil_simulado`, no solo lo que cambió: el guardado tiene rebote, así
  // que entre teclear y guardar el servidor todavía tiene el perfil viejo. Mandarlo completo hace
  // que el efecto sea exacto en todo momento, independientemente de si ya se guardó.
  const clave = JSON.stringify(perfil)
  useEffect(() => {
    const corte = new AbortController()
    const t = window.setTimeout(() => {
      enviar<RespuestaBusqueda>('/api/oportunidades/buscar',
                                { limit: 3, orden: 'rel', solo_mio: true, perfil_simulado: perfil },
                                corte.signal)
        .then(setDatos).catch(() => { if (!corte.signal.aborted) setDatos(null) })
      // El universo abierto, sin filtros ni perfil: es el «de N abiertos» del denominador.
      enviar<RespuestaBusqueda>('/api/oportunidades/buscar', { limit: 1 }, corte.signal)
        .then((r) => setUniverso(r.total)).catch(() => undefined)
      // Y el «bajarían a ~X» de declarar la capacidad máxima, medido y no estimado a ojo. Solo se
      // pide si falta: si ya está declarada, la frase no tendría nada que ofrecer.
      if (perfil.max === null && rup?.capacidad_max_pesos) {
        enviar<RespuestaBusqueda>('/api/oportunidades/buscar', {
          limit: 1, solo_mio: true,
          perfil_simulado: { ...perfil, max: Math.round(rup.capacidad_max_pesos / 1e6) },
        }, corte.signal).then((r) => setConMaximo(r.total)).catch(() => undefined)
      } else {
        setConMaximo(null)
      }
    }, 400)
    return () => { window.clearTimeout(t); corte.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, rup?.capacidad_max_pesos])

  const sirven = datos?.total ?? null
  const mejores = datos?.procesos ?? []

  return (
    <aside className="perfil-efecto" aria-label="El efecto de tu perfil">
      <section>
        <h2 className="rotulo">Con este perfil, hoy</h2>
        {sirven === null ? (
          <p className="efecto-vacio">Calculando…</p>
        ) : (
          <>
            <p className="efecto-cifra">
              <span className="cifra">{entero(sirven)}</span>
              <span>{sirven === 1 ? 'proceso te sirve' : 'procesos te sirven'}</span>
            </p>
            <p className="efecto-nota">
              {universo !== null && <>de {entero(universo)} abiertos. </>}
              {conMaximo !== null
                ? <>Con la capacidad máxima declarada bajarían a {entero(conMaximo)}, pero mejor
                    ordenados: se van los que no podrías ejecutar.</>
                : sirven === 0
                  ? <>Todavía no hay nada que se parezca a lo que declaraste. Cargar el RUP o
                      escribir tu NIT es lo que más cambia este número.</>
                  : <>Este número es de triaje: dice dónde mirar primero, no qué vas a ganar.</>}
            </p>
          </>
        )}
      </section>

      {mejores.length > 0 && (
        <section>
          <h2 className="rotulo">Los tres mejores ahora mismo</h2>
          <ul className="mejores">
            {mejores.map((p) => (
              <li key={p.id}>
                <Link to={`/proceso/${p.id}`}>
                  <b className="cifra">{p._afinidad ?? '—'}</b>
                  <span>
                    {/* `descripcion` y no `objeto`: en esta fuente `objeto` suele traer solo el
                        código de referencia del proceso. */}
                    <span className="mejores-objeto" title={p.descripcion || p.objeto}>
                      {normalizarObjeto(p.descripcion || p.objeto || p.id,
                                        [datos?.entidades?.[p.entidad_nit]?.ciudad,
                                         datos?.entidades?.[p.entidad_nit]?.departamento])}
                    </span>
                    <span className="mejores-pie">
                      {datos?.entidades?.[p.entidad_nit]?.nombre ?? 'Entidad sin nombre'} ·{' '}
                      {pesos(p.precio_base)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="rotulo">Qué mejoraría el orden</h2>
        <ul className="mejoras">
          {mejoras(perfil, rup).map((m) => (
            <li key={m.texto} className={`mejora-${m.tono}`}>
              <span className="mejora-marca" aria-hidden="true">
                {m.tono === 'hecho' ? <IconoSi /> : m.orden}
              </span>
              <span>{m.texto}</span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="rotulo">Datos de la empresa</h2>
        <dl className="datos-empresa">
          <Fila k="Razón social" v={perfil.razon_social || rup?.empresa || null} />
          <Fila k="NIT" v={perfil.nit || rup?.nit || null} />
          <Fila k="Tamaño" v={rup?.perfil?.tamano_empresa ?? null} />
          <Fila k="Domicilio" v={[rup?.perfil?.municipio, rup?.perfil?.departamento]
            .filter(Boolean).join(', ') || null} />
        </dl>
        <p className="efecto-nota">
          Tomado de tu RUP y de tu historial en SECOP. Si algo está mal, corrígelo en la fuente y
          vuelve a cargar el PDF.
        </p>
      </section>
    </aside>
  )
}

type Mejora = { texto: string; tono: 'urgente' | 'medio' | 'hecho'; orden: number }

/** Los huecos del perfil, priorizados por cuánto mueven la afinidad, y lo ya resuelto al final. */
function mejoras(perfil: Perfil, rup: RupDestilado | null): Mejora[] {
  const fuera: Mejora[] = []
  let n = 0
  if (!rup?.unspsc?.length) {
    fuera.push({ orden: ++n, tono: 'urgente',
                 texto: 'Cargar tu RUP o escribir tu NIT — es lo que más pesa: sin eso comparamos por sectores generales.' })
  }
  if (perfil.max === null) {
    fuera.push({ orden: ++n, tono: n === 1 ? 'urgente' : 'medio',
                 texto: 'Declarar tu capacidad máxima — saca los procesos que no podrías ejecutar.' })
  }
  if (perfil.deps.length === 0) {
    fuera.push({ orden: ++n, tono: 'medio',
                 texto: 'Decir dónde trabajas — no esconde nada, sube los de tus departamentos.' })
  }
  if (perfil.min === null) {
    fuera.push({ orden: ++n, tono: 'medio',
                 texto: 'Declarar tu contrato mínimo — baja los que no pagan las horas de propuesta.' })
  }
  if (rup?.unspsc?.length) {
    fuera.push({ orden: 0, tono: 'hecho',
                 texto: `Actividad cargada: la afinidad usa tus ${rup.unspsc.length} clases, no sectores generales.` })
  }
  if (perfil.deps.length > 0) {
    fuera.push({ orden: 0, tono: 'hecho',
                 texto: `Territorio declarado: ${perfil.deps.slice(0, 3).join(', ')}${perfil.deps.length > 3 ? '…' : ''}.` })
  }
  return fuera
}

function Fila({ k, v }: { k: string; v: string | null }) {
  return (
    <>
      <dt>{k}</dt>
      <dd className={v ? '' : 'sin-declarar'}>{v ?? 'sin declarar'}</dd>
    </>
  )
}

/* ── piezas compartidas ───────────────────────────────────────────────────── */

function Seccion({ titulo, sub, children }: {
  titulo: string
  sub: string
  children?: React.ReactNode
}) {
  return (
    <section className="perfil-seccion">
      <div className="perfil-seccion-cabeza">
        <h2>{titulo}</h2>
        <p>{sub}</p>
      </div>
      {children}
    </section>
  )
}

/** El mismo interruptor de la ficha. Botón real: se llega con tabulador y lo anuncia `aria-pressed`. */
function Palanca({ puesta, onCambiar, etiqueta }: {
  puesta: boolean
  onCambiar: () => void
  etiqueta: string
}) {
  return (
    <button type="button" className="interruptor" aria-pressed={puesta} onClick={onCambiar}>
      <span className={`palanca${puesta ? ' puesta' : ''}`} aria-hidden="true"><i /></span>
      <span>{etiqueta}</span>
    </button>
  )
}

/** «hace 2 minutos». Sin fecha exacta: lo que importa es si se guardó, no cuándo exactamente. */
function hace(ts: number | null): string {
  if (!ts) return 'Sin guardar todavía'
  const seg = Math.max(0, Math.round(Date.now() / 1000 - ts))
  if (seg < 60) return 'Guardado hace un momento'
  const min = Math.round(seg / 60)
  if (min < 60) return `Guardado hace ${min} ${min === 1 ? 'minuto' : 'minutos'}`
  const h = Math.round(min / 60)
  if (h < 24) return `Guardado hace ${h} ${h === 1 ? 'hora' : 'horas'}`
  const d = Math.round(h / 24)
  return `Guardado hace ${d} ${d === 1 ? 'día' : 'días'}`
}

export const _pruebas = { queFalta, contarFaltantes, mejoras, hace, tieneActividad, estadoNit }
